import { useEffect, useState } from "react";
import "./landing-portal.css";
import sangriaMark from "../assets/pittsboro_kiwanis_logo.png";
import { api } from "../lib/api";

const BADGE_TINTS = ["slate", "rose", "emerald", "amber", "purple"];

const fmtBadgeDate = (iso) => {
  const d = new Date(iso + "T00:00:00");
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "TODAY";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" }).toUpperCase();
};

const fmtBadgeTime = (hms) => {
  if (!hms) return "All day";
  const [h, m] = hms.split(":");
  const d = new Date();
  d.setHours(Number(h), Number(m || 0), 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

/**
 * Public landing portal — the very first page anyone visiting
 * sangria.studio sees. Shows the Pittsboro Kiwanis logo (which opens the
 * login modal) plus a public agenda of upcoming events pulled from
 * /api/public/eventcal (no auth required).
 */
export default function LandingPortal({ onEnterBookkeeping }) {
  const [events, setEvents] = useState(null); // null = loading, [] = empty
  const [error, setError] = useState("");

  // Load the two Google Fonts we need (Open Sans + Fraunces). Done
  // once at mount; if the user navigates away to <Login> the fonts
  // remain cached for instant return.
  useEffect(() => {
    const id = "landing-portal-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Open+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    api.listPublicEventcal()
      .then((rows) => setEvents(rows))
      .catch(() => setError("Couldn't load upcoming events right now."));
  }, []);

  return (
    <div className="landing-portal" data-testid="landing-portal">
      <div className="page-halo" aria-hidden="true" />

      <header className="site-header site-header--logo-only">
        <button
          type="button"
          className="brand brand--enlarged"
          aria-label="Log in to Pittsboro Kiwanis"
          data-testid="portal-login-cta"
          onClick={onEnterBookkeeping}
        >
          <img src={sangriaMark} alt="Pittsboro Kiwanis" className="brand__logo" />
        </button>
      </header>

      <main>
        <section className="hero" id="about">
          <h1 className="hero__title">
            Kiwanis is a global organization of volunteers dedicated to
            improving the World one child and one community at a time.
          </h1>
        </section>

        <section
          id="events"
          className="events-agenda"
          aria-label="Upcoming events"
          data-testid="events-agenda"
        >
          <h2 className="events-agenda__title">
            Upcoming Events <span className="events-agenda__hint">(click to add to calendar)</span>
          </h2>

          {events === null && !error && (
            <p className="events-agenda__status" data-testid="events-agenda-loading">Loading events…</p>
          )}
          {error && (
            <p className="events-agenda__status" data-testid="events-agenda-error">{error}</p>
          )}
          {events && events.length === 0 && (
            <p className="events-agenda__status" data-testid="events-agenda-empty">
              No upcoming events right now — check back soon.
            </p>
          )}

          {events && events.length > 0 && (
            <ul className="events-agenda__list">
              {events.map((ev, i) => (
                <li
                  key={ev.eventcal_seq}
                  style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}
                >
                  <div className="event-card">
                    <a
                      className="event-card__link"
                      href={api.publicEventcalIcsUrl(ev.eventcal_seq)}
                      title="Click to add to calendar"
                      data-testid={`event-card-${ev.eventcal_seq}`}
                    >
                      <div className={`event-card__badge event-card__badge--${BADGE_TINTS[i % BADGE_TINTS.length]}`}>
                        <span className="event-card__badge-date">{fmtBadgeDate(ev.eventcal_dt)}</span>
                        <span className="event-card__badge-time">{fmtBadgeTime(ev.eventcal_frm)}</span>
                      </div>
                      <div className="event-card__body">
                        <h3 className="event-card__title">{ev.eventcal_ttl}</h3>
                        {ev.eventcal_dsc && <p className="event-card__desc">{ev.eventcal_dsc}</p>}
                      </div>
                    </a>
                    <button
                      type="button"
                      className="event-card__signup"
                      data-testid={`event-card-signup-${ev.eventcal_seq}`}
                      disabled={!ev.eventcal_url}
                      style={ev.eventcal_url ? undefined : { visibility: "hidden", pointerEvents: "none" }}
                      title={ev.eventcal_url ? "Open sign-up link in a new tab" : ""}
                      onClick={() => { if (ev.eventcal_url) window.open(ev.eventcal_url, "_blank", "noopener,noreferrer"); }}
                    >SignUp</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      <footer className="site-footer">
        <nav className="site-footer__nav" aria-label="Footer">
          <a href="#events" className="site-footer__link" data-testid="footer-services">Services</a>
          <a href="#about" className="site-footer__link" data-testid="footer-about">About</a>
          <a
            href="mailto:hello@sangria.studio"
            className="site-footer__link"
            data-testid="footer-contact"
          >
            Contact
          </a>
        </nav>
        <div className="site-footer__meta">
          <span className="site-footer__brand">Pittsboro Kiwanis</span>
          <span className="site-footer__sep" aria-hidden="true">·</span>
          <a className="site-footer__link" href="mailto:hello@sangria.studio">
            hello@sangria.studio
          </a>
          <span className="site-footer__sep" aria-hidden="true">·</span>
          <span className="site-footer__rights">
            © {new Date().getFullYear()} Pittsboro Kiwanis
          </span>
        </div>
      </footer>
    </div>
  );
}
